import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { Product } from '@chancenwerk/_cms/products/entities/Product.js';
import { Adresse } from '@chancenwerk/db/adresse.js';
import { buildFundingYearStringArray } from '@chancenwerk/funding/api/helper.js';
import { DateTime } from 'luxon';
import { CRMFlags } from '../../../db/CRMFlags.js';
import { Bundesland } from '../../../db/enums/enums.js';
import { ChancenwerkUser, PERSON_BLACKLIST } from '../../users/entities/ChancenwerkUser.js';
import type { KooperationsPartner } from './KooperationsPartner.js';

export enum PartnerContactType {
  EMAIL = 'E-Mail',
  TEL = 'Telefonisch',
  PERSONAL = 'Persönlich',
}

export enum PartnerStateOfContactRequest {
  SENDED = 'Gesprächsanfrage gesendet',
  WAS_HELD = 'Gesprächstermin hat stattgefunden',
  TERMINATED = 'Gespräch terminiert',
  ARRANGEMENT = 'Terminabsprache',
  INTERESTED = 'Interesse am Gespräch',
  NOT_INTERESTED = 'kein Interesse am Gespräch',
  NO_CALLBACK = 'keine Rückmeldung',
}

export enum ProductInterest {
  YES = 'Ja',
  NO = 'Nein',
  MAYBE = 'perspektivisch',
}

/**
 * @entity PartnerPerProductStatistics
 */
@Entity()
export class PartnerPerProductStatistics extends AdornisEntity {
  public static override _class = 'PartnerPerProductStatistics';

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  /**
   * describes the usage of the product. Normally this would be the number of users for an KPF with this product.
   * The product CHANCENhefte uses the number of things sold from the logistik
   */
  @Field(type => Int, { default: v => v ?? 0 })
  public usage: Maybe<number>;

  /**
   * this is the manual usage written down in the ragProducts of a partner. This is only filled if
   */
  @Field(type => Int, { default: v => v ?? 0 })
  public ragManualUsage: Maybe<number>;
}

/**
 * @entity PartnerAcquisitionContactHistoryItem
 */
@Entity()
export class PartnerAcquisitionContactHistoryItem extends AdornisEntity {
  public static override _class = 'PartnerAcquisitionContactHistoryItem';

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(type => DateTime)
  public contactDate: Maybe<DateTime>;

  @Field(type => String)
  public contactType: Maybe<PartnerContactType>;

  @Field(type => String)
  public contactProductName: Maybe<string>;

  @Field(type => String)
  public contactReason: Maybe<string>;
}

/**
 * @entity PartnerAdditionalAgreement
 */
@Entity()
export class PartnerAdditionalAgreement extends AdornisEntity {
  public static override _class = 'PartnerAdditionalAgreement';

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(type => String)
  public productClass: Maybe<string>;

  @Field(type => DateTime)
  public sentAt: Maybe<DateTime>;

  @Field(type => DateTime)
  public backAt: Maybe<DateTime>;
}

/**
 * @entity PartnerSocialIndicator
 */
@Entity()
export class PartnerSocialIndicator extends AdornisEntity {
  public static override _class = 'PartnerSocialIndicator';

  @Field(type => String, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(type => Int)
  public value: Maybe<number>;

  @Field(type => String)
  public year: Maybe<string>;

  public override toString() {
    return `${this.value} (${this.year})`;
  }
}

/**
 * @entity Partner
 */
@Entity()
export class Partner extends MongoEntity {
  public static override _class = 'Partner';
  public static override _collectionName = 'partners';

  public static override get allFields() {
    return selectionSet(() => this, 3);
  }

  @DataLoadedField(
    type => [Partner],
    ({ _id }) => ({
      parentID: _id,
    }),
  )
  public subdivisions: Maybe<Partner[]>;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  public lastChanged: Maybe<DateTime>;

  @Field(type => CRMFlags, { default: v => v ?? new CRMFlags({}) })
  public flags: Maybe<CRMFlags>;

  @Field(type => String)
  public name: Maybe<string>;

  @Field(type => String)
  public url: Maybe<string>;

  @Field(type => String)
  public evaluationID: Maybe<string>;

  @validate<Partner>(({ value, key, source, step, target }) => {
    if (!value)
      throw new ValidationError('Bitte geben Sie ein Kürzel für die Schule an.', {
        key,
        source,
        translationKey: 'kuerzelPartner',
      });
  })
  @Field(type => String)
  public kuerzel: Maybe<string>;

  @Field(type => String)
  public bundesland: Maybe<Bundesland>;

  @Field(type => String)
  public telefonNumber: Maybe<string>;

  @Field(type => String)
  public secondTelefone: Maybe<string>;

  @Field(type => String)
  public email: Maybe<string>;

  @Field(type => String)
  public contactSource: Maybe<string>;

  @Field(type => ID)
  public contactSourcePersonID: Maybe<string>;

  @DataLoadedField(type => ChancenwerkUser, ({ contactSourcePersonID }) => contactSourcePersonID)
  public contactSourcePerson: Maybe<ChancenwerkUser>;

  @Field(type => ID)
  public personalSignOnChrstimasCardFromID: Maybe<string>;

  @DataLoadedField(
    type => ChancenwerkUser,
    ({ personalSignOnChrstimasCardFromID }) => personalSignOnChrstimasCardFromID,
  )
  public personalSignOnChrstimasCardFrom: Maybe<ChancenwerkUser>;

  @Field(type => [PartnerSocialIndicator])
  public socialIndicators: Maybe<PartnerSocialIndicator[]>;

  @Field(type => DateTime)
  public acquisitionStartedDate: Maybe<DateTime>;

  @Field(type => DateTime, {
    resolve: function (this: Partner) {
      return async () => {
        const KPF = (await import('../../kpf/entities/KPF.js')).KPF;
        return await loadMongoData(KPF, { schoolID: this._id })
          .then(kpfs =>
            kpfs
              .map(kpf => kpf.startDatum)
              .filter(d => d !== undefined && d !== null)
              .sort((a, b) => a.getTime() - b.getTime())
              .at(0),
          )
          .then(md => (md ? DateTime.fromJSDate(md) : undefined));
      };
    },
  })
  public startDate: Maybe<DateTime>;

  @Field(type => DateTime)
  public pausedAt: Maybe<DateTime>;

  @Field(type => DateTime)
  public endDate: Maybe<DateTime>;

  @Field(type => String, {
    resolve: function (this: Partner) {
      return async () => {
        if (this.startDate && this.endDate) {
          const startDate = DateTime.fromJSDate(this.startDate as unknown as Date);
          const endDate = DateTime.fromJSDate(this.endDate as unknown as Date);
          return `${startDate.toFormat('dd.LL.yyyy')} - ${endDate.toFormat('dd.LL.yyyy')}`;
        }

        const kpf = await (await import('../../kpf/entities/KPF.js')).KPF;
        const kpfs = await loadMongoData(kpf, { schoolID: this._id });

        let resultStartDate = this.startDate
          ? DateTime.isDateTime(this.startDate)
            ? this.startDate
            : DateTime.fromJSDate(this.startDate)
          : null;
        let resultEndDate = this.endDate
          ? DateTime.isDateTime(this.endDate)
            ? this.endDate
            : DateTime.fromJSDate(this.endDate)
          : null;

        const allKPFsEnded = kpfs.every(kpf => kpf.endDatum);

        let lowestStartdate = kpfs[0]?.startDatum;
        let highestEnddate = kpfs[0]?.endDatum;
        for (const kpf of kpfs) {
          if (kpf.startDatum && (!lowestStartdate || kpf.startDatum < lowestStartdate)) {
            lowestStartdate = kpf.startDatum;
          }
          if (kpf.endDatum && (!highestEnddate || kpf.endDatum > highestEnddate)) {
            highestEnddate = kpf.endDatum;
          }
        }

        if (!resultStartDate && lowestStartdate) {
          resultStartDate = DateTime.fromJSDate(lowestStartdate);
        }

        if (!resultEndDate && highestEnddate && allKPFsEnded) {
          resultEndDate = DateTime.fromJSDate(highestEnddate);
        }

        if (resultStartDate && resultEndDate) {
          return `${resultStartDate.toFormat('dd.LL.yyyy')} - ${resultEndDate.toFormat('dd.LL.yyyy')}`;
        }

        if (resultStartDate) return `Gestartet: ${resultStartDate.toFormat('dd.LL.yyyy')}`;
        if (resultEndDate) return `Beendet: ${resultEndDate.toFormat('dd.LL.yyyy')}`;
        return 'Kein Zeitraum angegeben!';
      };
    },
  })
  public zusammenArbeitStringForDetails: Maybe<string>;

  @Field(type => String)
  public endedReason: Maybe<string>;

  @Field(type => Int)
  public sizeOfSchoolInNumberOfStudents: Maybe<number>;

  @Field(type => Int)
  public numberOfTeachers: Maybe<number>;

  @Field(type => String)
  public specialCasesAndMisc: Maybe<string>;

  @Field(type => String)
  public salutationInLetter: Maybe<string>;

  @Field(type => String)
  public contactReason: Maybe<string>;

  @Field(type => ID)
  public acquisitionMultiplicatorPersonID: Maybe<string>;

  @DataLoadedField(type => ChancenwerkUser, ({ acquisitionMultiplicatorPersonID }) => acquisitionMultiplicatorPersonID)
  public acquisitionMultiplicatorPerson: Maybe<ChancenwerkUser>;

  @Field(type => String)
  public acquisitionMultiplicatorPersonPosition: Maybe<string>;

  @Field(type => [PartnerAcquisitionContactHistoryItem], {
    default: v => v ?? [new PartnerAcquisitionContactHistoryItem({})],
  })
  public acquisitioContactHistory: Maybe<PartnerAcquisitionContactHistoryItem[]>;

  @Field(type => DateTime)
  public acquisitionMeetingDate: Maybe<DateTime>;

  @Field(type => String)
  public acquisitionReasonForCancellation: Maybe<string>;

  @Field(type => DateTime)
  public acquisitionTerminationDate: Maybe<DateTime>;

  @Field(type => DateTime)
  public acquisitionHandOverEmail: Maybe<DateTime>;

  @Field(type => String)
  public stateOfContactRequest: Maybe<string>;

  @Field(type => String)
  public stateOfContactRequestMoreDetail: Maybe<string>;

  @Field(type => ID)
  public endedTroughPersonID: Maybe<string>;

  @DataLoadedField(type => ChancenwerkUser, ({ endedTroughPersonID }) => endedTroughPersonID)
  public endedTroughPerson: Maybe<ChancenwerkUser>;

  @Field(type => String)
  public roleFromPersonEndedTrough: Maybe<string>;

  @Field(type => Adresse, { default: v => v ?? new Adresse({}) })
  public address: Maybe<Adresse>;

  @Field(type => String)
  public postfach: Maybe<string>;

  @Field(type => String)
  public postfachPlz: Maybe<string>;

  @Field(type => String)
  public postfachOrt: Maybe<string>;

  @Field(type => ID)
  public personDidAcquisitionMeetingID: Maybe<string>;

  @DataLoadedField(type => ChancenwerkUser, ({ personDidAcquisitionMeetingID }) => personDidAcquisitionMeetingID)
  public personDidAcquisitionMeeting: Maybe<ChancenwerkUser>;

  @Field(type => ID)
  public handoverEmailWrittenByID: Maybe<string>;

  @DataLoadedField(type => ChancenwerkUser, ({ handoverEmailWrittenByID }) => handoverEmailWrittenByID)
  public handoverEmailWrittenBy: Maybe<ChancenwerkUser>;

  @Field(type => DateTime)
  public cooperationContractSentOn: Maybe<DateTime>;

  @Field(type => DateTime)
  public cooperationContractBackOn: Maybe<DateTime>;

  @Field(type => [Product], { default: v => v ?? [] })
  public ragProducts: Maybe<Product[]>;

  public ensureRAGProduct(_class: string) {
    if (!this.ragProducts) this.ragProducts = [];
    if (!this.ragProducts.find(rag => rag._class === _class)) {
      this.ragProducts.push(
        new (baseqlMetaData.entities.resolveNode(_class).type() as typeof Product)({ _class, ragYearInformation: [] }),
      );
    }

    const index = this.ragProducts.findIndex(rag => rag._class === _class);
    const product = this.ragProducts[index];

    return {
      index,
      product,
    };
  }

  @Field(type => [PartnerAdditionalAgreement], { default: v => v ?? [] })
  public additionalAgreements: Maybe<PartnerAdditionalAgreement[]>;

  @Dependencies<typeof Partner>({
    classes: async () => {
      return [(await import('../../kpf/entities/KPF.js')).KPF];
    },
    ids: async function (this) {
      const { KPF } = await import('../../kpf/entities/KPF.js');
      const kpfs = await loadMongoData(KPF, { schoolID: this._id });
      return kpfs.map(kpf => kpf._id);
    },
  })
  @Field(type => [String], {
    resolve: function (this: Partner) {
      return async () => {
        // KPFs
        const { KPF } = await import('../../kpf/entities/KPF.js');
        const kpfs = await loadMongoData(KPF, { schoolID: this._id });

        return kpfs.map(kpf => kpf.produkt._class.slice(-3)).concat(this.ragProducts?.map(rag => rag._class.slice(-3)));
      };
    },
  })
  public productStrings: Maybe<string[]>;

  @Field(type => [PartnerPerProductStatistics], {
    resolve: function (this: Partner) {
      return async () => {
        // KPFs

        const allYears = buildFundingYearStringArray();

        let result: PartnerPerProductStatistics[] = [];

        const getUsageStatisticsForSchoolyear = (
          await import('@chancenwerk/master-data-management/partner/api/getUsageStatisticsForSchoolyear.js')
        ).getUsageStatisticsForSchoolyear;
        for (const year of allYears) {
          result = result.concat(await getUsageStatisticsForSchoolyear(year, this));
        }

        return result;
      };
    },
  })
  public productStatistics: Maybe<PartnerPerProductStatistics[]>;

  static override async transformBeforeUpdate(
    oldEntity: { _id?: string | undefined; _class?: string | undefined },
    update: Partner,
  ): Promise<void> {
    update.lastChanged = DateTime.now();

    return super.beforeUpdate(oldEntity, update);
  }

  static override async beforeRemove(id: string, entityData: any): Promise<void> {
    const { KPF } = await import('../../kpf/entities/KPF.js');
    const kpfColl = await getRawCollection(KPF._collectionName);
    const kpfCount = await kpfColl.countDocuments({ schoolID: id });
    if (kpfCount === 1)
      throw new Error(
        'Kooperationspartner kann nicht gelöscht werden! Es existiert noch ' + kpfCount + ' KPF für diesen Partner.',
      );
    if (kpfCount > 1)
      throw new Error(
        'Kooperationspartner kann nicht gelöscht werden! Es existieren noch ' + kpfCount + ' KPFs für diesen Partner.',
      );
    return super.beforeRemove(id, entityData);
  }

  private static async checkDuplicatePartnerKuerzel(partner: Partner) {
    if (!A.isServer) throw new ValidationError('This is a serverside helper only');

    const partnerCollection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
    const count = await partnerCollection.countDocuments({
      kuerzel: partner.kuerzel?.trim(),
    });
    if (count >= 1) throw new ValidationError(`Die Schule mit diesem Kürzel gibt es bereits.`);
    return true;
  }

  static override async beforeCreate(entity: Partner): Promise<void> {
    await super.beforeCreate(entity);
    await this.checkDuplicatePartnerKuerzel(entity);
  }

  override toString() {
    return this.name;
  }

  public get type(): null | KooperationsPartner {
    return null;
  }
}

export const PARTNER_BLACKLIST = {
  flags: {
    _class: 1,
    getAll: 1,
    getsInfoAnniversaryRecord: {
      user: {
        ...PERSON_BLACKLIST,
        butActive: 1,
      },
      userID: 1,
    },
    getsChristmascardRecord: {
      user: {
        ...PERSON_BLACKLIST,
        butActive: 1,
      },
      userID: 1,
    },
    getsYearlyReportRecord: {
      user: {
        ...PERSON_BLACKLIST,
        butActive: 1,
      },
      userID: 1,
    },
  },
  parentID: 1,
  _class: 1,
  productStrings: 1,
  productStatistics: 1,
  interestedProducts: 1,
  ragProducts: 1,
  handoverEmailWrittenByID: 1,
  handoverEmailWrittenBy: 1,
  personDidAcquisitionMeetingID: 1,
  personDidAcquisitionMeeting: 1,
  acquisitionMultiplicatorPersonID: 1,
  acquisitionMultiplicatorPerson: 1,
  acquisitionMultiplicatorPersonPosition: 1,
  contactSourcePersonID: 1,
  contactSourcePerson: 1,
  personalSignOnChrstimasCardFromID: 1,
  personalSignOnChrstimasCardFrom: 1,
  socialIndicators: 1,
  additionalAgreements: 1,
  endedTroughPersonID: 1,
  endedTroughPerson: 1,
  roleFromPersonEndedTrough: 1,
  acquisitioContactHistory: 1,
  address: {
    _class: 1,
    zipCity: {
      _class: 1,
      zip: 1,
      city: 1,
    },
  },
  media: 1,
  mediaID: 1,
  operative: 1,
  zugConfiguration: 1,
  schoolInformation: {
    abweichendeSchule: {
      subdivisions: 1,
    },
  },
};

PARTNER_BLACKLIST['parent'] = PARTNER_BLACKLIST;
